// src/layouts/SidebarLayout/components/Sidebar.tsx
import React from 'react';
import {
  Drawer,
  Box,
  IconButton,
  Typography,
  useTheme,
  useMediaQuery,
  alpha,
} from '@mui/material';
import {
  Menu as MenuIcon,
  ChevronLeft,
  ChevronRight,
  Home as HomeIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useSidebar } from '../../../contexts/SidebarContext';
import SidebarMenu from './SidebarMenu';
import { SidebarLink, LayoutConfig } from '../types';

interface SidebarProps {
  links: SidebarLink[];
  config: LayoutConfig;
}

const SidebarContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'collapsed',
})<{ collapsed: boolean }>(({ theme, collapsed }) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  background: theme.palette.mode === 'dark'
    ? `linear-gradient(180deg, #1A1A2E 0%, #16213E 100%)`
    : `linear-gradient(180deg, #ffffff 0%, ${alpha('#4CAF50', 0.03)} 100%)`,
  borderRight: `2px solid ${alpha('#4CAF50', 0.15)}`,
  overflow: 'hidden',
  position: 'relative',
  boxShadow: theme.palette.mode === 'dark'
    ? `8px 0 32px ${alpha('#4CAF50', 0.2)}, 4px 0 16px ${alpha(theme.palette.common.black, 0.4)}`
    : `8px 0 32px ${alpha('#4CAF50', 0.12)}`,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    width: '3px',
    height: '100%',
    background: 'linear-gradient(180deg, #4CAF50 0%, #00897B 50%, #26A69A 100%)',
    opacity: 0.6,
  },
}));

const SidebarHeader = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'collapsed',
})<{ collapsed: boolean }>(({ theme, collapsed }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: collapsed ? theme.spacing(2.5, 1) : theme.spacing(2.5, 2.5),
  minHeight: 80,
  borderBottom: `2px solid ${alpha('#4CAF50', 0.2)}`,
  position: 'relative',
  justifyContent: collapsed ? 'center' : 'space-between',
  background: theme.palette.mode === 'dark'
    ? `linear-gradient(135deg, ${alpha('#4CAF50', 0.2)} 0%, ${alpha('#00897B', 0.15)} 100%)`
    : `linear-gradient(135deg, ${alpha('#4CAF50', 0.08)} 0%, ${alpha('#00897B', 0.05)} 100%)`,
  boxShadow: `0 4px 12px ${alpha('#4CAF50', 0.15)}`,
}));

const LogoSection = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'collapsed',
})<{ collapsed: boolean }>(({ theme, collapsed }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: collapsed ? 0 : theme.spacing(1.5),
  flex: collapsed ? 'none' : 1,
  minWidth: 0,
  justifyContent: collapsed ? 'center' : 'flex-start',
}));

const CollapseButton = styled(IconButton)(({ theme }) => ({
  background: 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)',
  border: `2px solid ${alpha('#66BB6A', 0.5)}`,
  width: 36,
  height: 36,
  position: 'absolute',
  right: 8,
  top: '50%',
  transform: 'translateY(-50%)',
  boxShadow: `0 4px 16px ${alpha('#4CAF50', 0.4)}, 0 0 20px ${alpha('#4CAF50', 0.2)}`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    background: 'linear-gradient(135deg, #388E3C 0%, #4CAF50 100%)',
    borderColor: '#66BB6A',
    transform: 'translateY(-50%) scale(1.1) rotate(180deg)',
    boxShadow: `0 6px 24px ${alpha('#4CAF50', 0.6)}, 0 0 30px ${alpha('#4CAF50', 0.3)}`,
  },
  '& .MuiSvgIcon-root': {
    color: '#FFFFFF',
    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
  },
}));

const MenuContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
  padding: theme.spacing(1),
  '&::-webkit-scrollbar': {
    width: 8,
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: alpha('#1A1A2E', 0.3),
    borderRadius: 4,
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'linear-gradient(180deg, #4CAF50 0%, #00897B 100%)',
    borderRadius: 4,
    border: `2px solid ${alpha('#66BB6A', 0.2)}`,
    boxShadow: `0 0 10px ${alpha('#4CAF50', 0.4)}`,
    '&:hover': {
      background: 'linear-gradient(180deg, #66BB6A 0%, #388E3C 100%)',
      boxShadow: `0 0 15px ${alpha('#4CAF50', 0.6)}`,
    },
  },
}));

const Sidebar: React.FC<SidebarProps> = ({ links, config }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { collapsed, mobileOpen, toggleCollapse, toggleMobile, getWidth } = useSidebar();
  
  const sidebarWidth = getWidth();
  const isCollapsed = collapsed && !isMobile;

  const sidebarContent = (
    <SidebarContainer collapsed={isCollapsed}>
      <SidebarHeader collapsed={isCollapsed}>
        <LogoSection collapsed={isCollapsed}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 48,
              height: 48,
              borderRadius: '16px',
              background: 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)',
              color: '#FFFFFF',
              flexShrink: 0,
              boxShadow: `0 8px 24px ${alpha('#4CAF50', 0.5)}, 0 0 30px ${alpha('#4CAF50', 0.3)}`,
              position: 'relative',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: -2,
                borderRadius: '18px',
                background: 'linear-gradient(135deg, #66BB6A, #00897B)',
                zIndex: -1,
                opacity: 0.7,
                animation: 'neonPulse 2s ease-in-out infinite',
              },
              '&:hover': {
                transform: 'scale(1.05) rotate(5deg)',
                boxShadow: `0 12px 32px ${alpha('#4CAF50', 0.6)}, 0 0 40px ${alpha('#4CAF50', 0.4)}`,
              },
              '@keyframes neonPulse': {
                '0%, 100%': {
                  opacity: 0.7,
                },
                '50%': {
                  opacity: 1,
                },
              },
            }}
          >
            <HomeIcon sx={{ fontSize: 28, filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.4))' }} />
          </Box>
          {!isCollapsed && (
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 900,
                  background: 'linear-gradient(135deg, #4CAF50 0%, #00897B 50%, #2196F3 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontSize: '1.4rem',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  letterSpacing: '-0.5px',
                  filter: 'drop-shadow(0 2px 4px rgba(76, 175, 80, 0.3))',
                }}
              >
                RentPro
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#00897B',
                  fontSize: '0.75rem',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontWeight: 600,
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                }}
              >
                Property Management Suite
              </Typography>
            </Box>
          )}
        </LogoSection>
        
        {!isMobile && config.allowSidebarToggle && (
          <CollapseButton onClick={toggleCollapse} size="small">
            {isCollapsed ? (
              <ChevronRight sx={{ fontSize: 18 }} />
            ) : (
              <ChevronLeft sx={{ fontSize: 18 }} />
            )}
          </CollapseButton>
        )}
        
        {isMobile && (
          <IconButton onClick={toggleMobile} size="small">
            <MenuIcon />
          </IconButton>
        )}
      </SidebarHeader>

      <MenuContainer>
        <SidebarMenu links={links} collapsed={isCollapsed} />
      </MenuContainer>

      {/* Footer */}
      {!isCollapsed && (
        <Box
          sx={{
            p: 2.5,
            borderTop: `2px solid ${alpha('#4CAF50', 0.2)}`,
            textAlign: 'center',
            background: theme.palette.mode === 'dark'
              ? `linear-gradient(180deg, transparent 0%, ${alpha('#00897B', 0.1)} 100%)`
              : `linear-gradient(180deg, transparent 0%, ${alpha('#4CAF50', 0.05)} 100%)`,
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              width: '60%',
              height: '2px',
              background: 'linear-gradient(90deg, transparent 0%, #4CAF50 50%, transparent 100%)',
            },
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: '#00897B',
              fontSize: '0.7rem',
              fontWeight: 700,
              display: 'block',
              marginBottom: 0.5,
              letterSpacing: '0.5px',
            }}
          >
            v3.0.0 © 2025
          </Typography>
          <Typography
            variant="caption"
            sx={{
              background: 'linear-gradient(90deg, #4CAF50 0%, #00897B 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontSize: '0.65rem',
              fontWeight: 600,
            }}
          >
            RentPro Management
          </Typography>
        </Box>
      )}
    </SidebarContainer>
  );

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={toggleMobile}
        ModalProps={{
          keepMounted: true,
        }}
        PaperProps={{
          sx: {
            width: config.sidebarWidth.expanded,
            boxShadow: theme.shadows[8],
          },
        }}
      >
        {sidebarContent}
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="permanent"
      PaperProps={{
        sx: {
          width: sidebarWidth,
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.standard,
          }),
          overflowX: 'hidden',
          boxShadow: theme.shadows[2],
        },
      }}
    >
      {sidebarContent}
    </Drawer>
  );
};

export default Sidebar;